import "@glideapps/glide-data-grid/dist/index.css";

import DataEditor, {
  CompactSelection,
  GridCellKind,
  type DataEditorRef,
  type EditableGridCell,
  type GridCell,
  type GridColumn,
  type GridKeyEventArgs,
  type GridSelection,
  type Item,
  type Rectangle,
  type Theme,
} from "@glideapps/glide-data-grid";
import type { EexQuoteBook, EexQuoteRowComputed, EexQuoteRowInput } from "@chro/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Copy, Eraser, Redo2, Undo2 } from "lucide-react";
import { resolveTheme, useThemeStore } from "../../../stores/theme";
import { cn } from "../../../lib/utils";
import {
  QUOTE_COLUMN_BY_ID,
  type EditableQuoteRowKey,
  type QuoteColumnId,
  type QuoteColumnSpec,
  type QuoteLayout,
  type QuoteSnapshot,
} from "./model";

const EMPTY_SELECTION: GridSelection = {
  columns: CompactSelection.empty(),
  rows: CompactSelection.empty(),
};

interface QuoteGridProps {
  snapshot: QuoteSnapshot;
  book: EexQuoteBook;
  canEdit: boolean;
  canUndo: boolean;
  canRedo: boolean;
  onRowsChange(rows: EexQuoteRowInput[]): void;
  onLayoutChange(layout: QuoteLayout): void;
  onSelectedMonthsChange(months: string[]): void;
  onUndo(): void;
  onRedo(): void;
  onSave(): void;
  onOpenShortcuts(): void;
}

interface SelectedCell {
  col: number;
  row: number;
  column: QuoteColumnSpec;
}

interface SelectionStats {
  cells: number;
  editable: number;
  numeric: number;
  sum: number;
  average: number | null;
  min: number | null;
  max: number | null;
}

function selectionRectangles(selection: GridSelection): readonly Rectangle[] {
  if (!selection.current) return [];
  return [selection.current.range, ...selection.current.rangeStack];
}

function itemIsSelected(selection: GridSelection, col: number, row: number): boolean {
  if (selection.rows.hasIndex(row) || selection.columns.hasIndex(col)) return true;
  return selectionRectangles(selection).some(
    (rect) =>
      col >= rect.x &&
      col < rect.x + rect.width &&
      row >= rect.y &&
      row < rect.y + rect.height
  );
}

function cellsInSelection(
  selection: GridSelection,
  columns: readonly QuoteColumnSpec[],
  rowCount: number
): SelectedCell[] {
  const result: SelectedCell[] = [];
  const seen = new Set<string>();
  const add = (col: number, row: number) => {
    if (col < 0 || col >= columns.length || row < 0 || row >= rowCount) return;
    const key = `${col}:${row}`;
    if (seen.has(key)) return;
    seen.add(key);
    result.push({ col, row, column: columns[col]! });
  };
  for (const rect of selectionRectangles(selection)) {
    for (let row = rect.y; row < rect.y + rect.height; row += 1) {
      for (let col = rect.x; col < rect.x + rect.width; col += 1) add(col, row);
    }
  }
  for (const row of selection.rows) {
    for (let col = 0; col < columns.length; col += 1) add(col, row);
  }
  for (const col of selection.columns) {
    for (let row = 0; row < rowCount; row += 1) add(col, row);
  }
  return result;
}

function parsePastedNumber(value: string): number | null {
  const text = value.trim().replace(/\s/g, "");
  if (!text) return null;
  const normalized = text.includes(",") && text.includes(".")
    ? text.lastIndexOf(",") > text.lastIndexOf(".")
      ? text.replaceAll(".", "").replace(",", ".")
      : text.replaceAll(",", "")
    : text.replace(",", ".");
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

function numberFromCell(cell: EditableGridCell): number | null | undefined {
  if (cell.kind === GridCellKind.Number) return cell.data ?? null;
  if (cell.kind === GridCellKind.Text) {
    if (!cell.data.trim()) return null;
    return parsePastedNumber(cell.data) ?? undefined;
  }
  return undefined;
}

function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}

function visibleColumnSpecs(layout: QuoteLayout): QuoteColumnSpec[] {
  const hidden = new Set(layout.hiddenColumns);
  return layout.columnOrder
    .filter((id) => !hidden.has(id) && (layout.showShapePnl || id !== "shapePnlRon"))
    .map((id) => QUOTE_COLUMN_BY_ID.get(id))
    .filter((column): column is QuoteColumnSpec => column !== undefined);
}

function decimalPlaces(column: QuoteColumnSpec, snapshot: QuoteSnapshot): number {
  if (column.decimals === "price") return snapshot.settings.priceDecimals;
  if (column.decimals === "fx") return snapshot.settings.fxDecimals;
  return column.decimals;
}

const NUMBER_FORMATTERS = new Map<number, Intl.NumberFormat>();
const MONTH_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  month: "short",
  year: "2-digit",
  timeZone: "UTC",
});

function formatNumber(value: number | null, decimals: number): string {
  if (value == null || !Number.isFinite(value)) return "";
  let formatter = NUMBER_FORMATTERS.get(decimals);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    NUMBER_FORMATTERS.set(decimals, formatter);
  }
  return formatter.format(value);
}

function totalForColumn(book: EexQuoteBook, id: QuoteColumnId): number | null {
  if (id === "mwh") return book.totals.totalMwh;
  if (id === "shapePnlRon") return book.totals.shapePnlRon;
  if (id in book.totals) {
    const value = book.totals[id as keyof typeof book.totals];
    return typeof value === "number" ? value : null;
  }
  return null;
}

function updateRows(
  rows: readonly EexQuoteRowInput[],
  changes: readonly { row: number; key: EditableQuoteRowKey; value: number | null }[]
): EexQuoteRowInput[] {
  if (changes.length === 0) return [...rows];
  const next = rows.map((row) => ({ ...row }));
  for (const change of changes) {
    const row = next[change.row];
    if (row) row[change.key] = change.value;
  }
  return next;
}

export function QuoteGrid({
  snapshot,
  book,
  canEdit,
  canUndo,
  canRedo,
  onRowsChange,
  onLayoutChange,
  onSelectedMonthsChange,
  onUndo,
  onRedo,
  onSave,
  onOpenShortcuts,
}: QuoteGridProps) {
  const gridRef = useRef<DataEditorRef>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const themePreference = useThemeStore((state) => state.preference);
  const resolvedTheme = resolveTheme(themePreference);
  const specs = useMemo(() => visibleColumnSpecs(snapshot.layout), [snapshot.layout]);
  const rowMonthsKey = useMemo(
    () => snapshot.rows.map((row) => row.month).join("|"),
    [snapshot.rows]
  );
  const [liveWidths, setLiveWidths] = useState<Partial<Record<QuoteColumnId, number>>>({});
  const [selection, setSelection] = useState<GridSelection>(EMPTY_SELECTION);
  const [bulkValue, setBulkValue] = useState("");
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setSelection(EMPTY_SELECTION);
    onSelectedMonthsChange([]);
    setLiveWidths({});
  }, [
    snapshot.layout.columnOrder,
    snapshot.layout.columnWidths,
    snapshot.layout.hiddenColumns,
    snapshot.layout.showShapePnl,
    rowMonthsKey,
    onSelectedMonthsChange,
  ]);

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener("pointerdown", close);
    window.addEventListener("blur", close);
    return () => {
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("blur", close);
    };
  }, [contextMenu]);

  const columns = useMemo<GridColumn[]>(
    () =>
      specs.map((column) => ({
        id: column.id,
        title: column.shortTitle,
        group: column.group,
        width:
          liveWidths[column.id] ?? snapshot.layout.columnWidths[column.id] ?? column.width,
        hasMenu: false,
        style: column.tone === "input" ? "highlight" : "normal",
      })),
    [liveWidths, snapshot.layout.columnWidths, specs]
  );

  const selectedCells = useMemo(
    () => cellsInSelection(selection, specs, book.rows.length),
    [book.rows.length, selection, specs]
  );

  const handleSelectionChange = useCallback(
    (next: GridSelection) => {
      setSelection(next);
      const selectedRows = new Set(
        cellsInSelection(next, specs, book.rows.length).map((cell) => cell.row)
      );
      onSelectedMonthsChange(
        [...selectedRows]
          .sort((a, b) => a - b)
          .map((row) => book.rows[row]?.month)
          .filter((month): month is string => Boolean(month))
      );
    },
    [book.rows, onSelectedMonthsChange, specs]
  );

  const stats = useMemo<SelectionStats>(() => {
    const values: number[] = [];
    let editable = 0;
    for (const selected of selectedCells) {
      if (selected.column.editableKey) editable += 1;
      const value = book.rows[selected.row]?.[selected.column.accessor];
      if (typeof value === "number" && Number.isFinite(value)) values.push(value);
    }
    const sum = values.reduce((total, value) => total + value, 0);
    return {
      cells: selectedCells.length,
      editable,
      numeric: values.length,
      sum,
      average: values.length > 0 ? sum / values.length : null,
      min: values.length > 0 ? Math.min(...values) : null,
      max: values.length > 0 ? Math.max(...values) : null,
    };
  }, [book.rows, selectedCells]);

  const applyChanges = useCallback(
    (changes: readonly { row: number; key: EditableQuoteRowKey; value: number | null }[]) => {
      if (!canEdit || changes.length === 0) return;
      const bounded = changes.filter(
        (change) => change.value == null || (Number.isFinite(change.value) && Math.abs(change.value) <= 1_000_000_000)
      );
      if (bounded.length > 0) onRowsChange(updateRows(snapshot.rows, bounded));
    },
    [canEdit, onRowsChange, snapshot.rows]
  );

  const applyBulk = useCallback(
    (operation: "set" | "add" | "multiply" | "clear", forcedValue?: number) => {
      if (!canEdit) return;
      const parsed = forcedValue ?? parsePastedNumber(bulkValue);
      if (operation !== "clear" && parsed == null) return;
      const changes = selectedCells.flatMap((selected) => {
        const key = selected.column.editableKey;
        if (!key) return [];
        const current = snapshot.rows[selected.row]?.[key] ?? null;
        let value: number | null;
        if (operation === "clear") value = null;
        else if (operation === "set") value = parsed;
        else if (current == null) return [];
        else value = operation === "add" ? current + parsed! : current * parsed!;
        return [{ row: selected.row, key, value }];
      });
      applyChanges(changes);
      setContextMenu(null);
    },
    [applyChanges, bulkValue, canEdit, selectedCells, snapshot.rows]
  );

  const getCellContent = useCallback(
    ([colIndex, rowIndex]: Item): GridCell => {
      const column = specs[colIndex];
      if (!column) {
        return { kind: GridCellKind.Text, data: "", displayData: "", allowOverlay: false };
      }
      const totalRow = rowIndex === book.rows.length;
      if (column.id === "month") {
        const value = totalRow ? "Σ / VWAP" : book.rows[rowIndex]?.month ?? "";
        return {
          kind: GridCellKind.Text,
          data: value,
          displayData: totalRow ? value : formatMonth(value),
          allowOverlay: false,
          readonly: true,
          contentAlign: "left",
          themeOverride: totalRow ? totalCellTheme(resolvedTheme) : labelCellTheme(resolvedTheme),
          copyData: totalRow ? value : formatMonth(value),
        };
      }
      const value = totalRow
        ? totalForColumn(book, column.id)
        : numericValue(book.rows[rowIndex], column.accessor);
      const decimals = decimalPlaces(column, snapshot);
      const editable = Boolean(column.editableKey && canEdit && !totalRow);
      return {
        kind: GridCellKind.Number,
        data: value ?? undefined,
        displayData: formatNumber(value, decimals),
        allowOverlay: editable,
        readonly: !editable,
        allowNegative: column.id !== "mw",
        thousandSeparator: ",",
        decimalSeparator: ".",
        contentAlign: "right",
        copyData: value == null ? "" : String(value),
        themeOverride: totalRow
          ? totalCellTheme(resolvedTheme)
          : cellTheme(column, resolvedTheme),
      };
    },
    [book, canEdit, resolvedTheme, snapshot, specs]
  );

  const handleCellsEdited = useCallback(
    (edits: readonly { location: Item; value: EditableGridCell }[]) => {
      const changes = edits.flatMap(({ location: [col, row], value }) => {
        const key = specs[col]?.editableKey;
        if (!key || row < 0 || row >= snapshot.rows.length) return [];
        const parsed = numberFromCell(value);
        return parsed === undefined ? [] : [{ row, key, value: parsed }];
      });
      applyChanges(changes);
      return true;
    },
    [applyChanges, snapshot.rows.length, specs]
  );

  const handleDelete = useCallback(
    (nextSelection: GridSelection) => {
      if (!canEdit) return false;
      const changes = cellsInSelection(nextSelection, specs, book.rows.length).flatMap(
        (selected) =>
          selected.column.editableKey
            ? [{ row: selected.row, key: selected.column.editableKey, value: null }]
            : []
      );
      applyChanges(changes);
      return false;
    },
    [applyChanges, book.rows.length, canEdit, specs]
  );

  const handleGridKey = useCallback(
    (event: GridKeyEventArgs) => {
      const mod = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();
      if (mod && key === "s") {
        event.preventDefault();
        event.stopPropagation();
        onSave();
      } else if (mod && key === "z") {
        event.preventDefault();
        event.stopPropagation();
        if (event.shiftKey) onRedo();
        else onUndo();
      } else if (mod && key === "y") {
        event.preventDefault();
        event.stopPropagation();
        onRedo();
      } else if (mod && event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        applyBulk("set");
      } else if (event.key === "?") {
        event.preventDefault();
        onOpenShortcuts();
      }
    },
    [applyBulk, onOpenShortcuts, onRedo, onSave, onUndo]
  );

  const theme = useMemo<Partial<Theme>>(
    () => gridTheme(resolvedTheme),
    [resolvedTheme]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-10 flex-wrap items-center gap-2 border-x border-t border-border/70 bg-bg px-2 py-1.5 text-[11px]">
        <button
          type="button"
          onClick={onUndo}
          disabled={!canUndo}
          className="quote-grid-tool"
          title="Undo (Ctrl/Cmd+Z)"
        >
          <Undo2 className="h-3.5 w-3.5" /> Undo
        </button>
        <button
          type="button"
          onClick={onRedo}
          disabled={!canRedo}
          className="quote-grid-tool"
          title="Redo (Ctrl/Cmd+Shift+Z)"
        >
          <Redo2 className="h-3.5 w-3.5" /> Redo
        </button>
        <span className="h-5 w-px bg-border/70" />
        <span className="text-muted">
          {stats.cells > 0 ? `${stats.cells} selected · ${stats.editable} editable` : "Select cells to batch edit"}
        </span>
        {stats.numeric > 0 && (
          <span className="text-muted">
            Σ {formatNumber(stats.sum, 2)} · Avg {formatNumber(stats.average, 2)} · Min {formatNumber(stats.min, 2)} · Max {formatNumber(stats.max, 2)}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <input
            value={bulkValue}
            onChange={(event) => setBulkValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                applyBulk("set");
              }
            }}
            disabled={!canEdit || stats.editable === 0}
            inputMode="decimal"
            placeholder="batch value"
            aria-label="Batch value for selected editable cells"
            className="h-7 w-24 rounded border border-border bg-bg px-2 text-right font-mono text-xs outline-none focus:border-accent disabled:opacity-40"
          />
          <button type="button" onClick={() => applyBulk("set")} disabled={!canEdit || stats.editable === 0} className="quote-grid-tool">Set</button>
          <button type="button" onClick={() => applyBulk("add")} disabled={!canEdit || stats.editable === 0} className="quote-grid-tool">+</button>
          <button type="button" onClick={() => applyBulk("multiply")} disabled={!canEdit || stats.editable === 0} className="quote-grid-tool">×</button>
          <button type="button" onClick={() => applyBulk("clear")} disabled={!canEdit || stats.editable === 0} className="quote-grid-tool" title="Clear selected editable cells">
            <Eraser className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={onOpenShortcuts} className="quote-grid-tool" title="Keyboard shortcuts">?</button>
        </div>
      </div>

      <div
        ref={wrapperRef}
        className="relative min-h-0 flex-1 overflow-hidden border border-border/70 bg-bg"
      >
        <DataEditor
          ref={gridRef}
          columns={columns}
          rows={book.rows.length + 1}
          getCellContent={getCellContent}
          getCellsForSelection
          onCellsEdited={handleCellsEdited}
          onDelete={handleDelete}
          onPaste={canEdit}
          coercePasteValue={(value, cell) => {
            if (cell.kind !== GridCellKind.Number || cell.readonly) return undefined;
            if (!value.trim()) return { ...cell, data: undefined, displayData: "" };
            const parsed = parsePastedNumber(value);
            if (parsed == null || Math.abs(parsed) > 1_000_000_000) return undefined;
            return { ...cell, data: parsed, displayData: String(parsed) };
          }}
          gridSelection={selection}
          onGridSelectionChange={handleSelectionChange}
          rangeSelect="multi-rect"
          rowSelect="multi"
          columnSelect="multi"
          rangeSelectionBlending="mixed"
          rowSelectionBlending="mixed"
          columnSelectionBlending="mixed"
          rowSelectionMode="auto"
          rowMarkers={{ kind: "both", checkboxStyle: "square", startIndex: 1, width: 46 }}
          freezeColumns={1}
          freezeTrailingRows={1}
          fillHandle={canEdit}
          allowedFillDirections="any"
          editOnType={canEdit}
          trapFocus
          rowHeight={snapshot.layout.density === "compact" ? 27 : 34}
          headerHeight={32}
          groupHeaderHeight={26}
          smoothScrollX
          smoothScrollY
          minColumnWidth={56}
          maxColumnWidth={420}
          onColumnResize={(_, newSize, colIndex) => {
            const id = specs[colIndex]?.id;
            if (id) setLiveWidths((current) => ({ ...current, [id]: Math.round(newSize) }));
          }}
          onColumnResizeEnd={(_, newSize, colIndex) => {
            const id = specs[colIndex]?.id;
            if (!id) return;
            onLayoutChange({
              ...snapshot.layout,
              columnWidths: { ...snapshot.layout.columnWidths, [id]: Math.round(newSize) },
            });
          }}
          onColumnMoved={(from, to) => {
            if (from === 0 || to === 0) return;
            const movedVisible = moveItem(specs.map((column) => column.id), from, to);
            let index = 0;
            const visible = new Set(movedVisible);
            const nextOrder = snapshot.layout.columnOrder.map((id) =>
              visible.has(id) ? movedVisible[index++]! : id
            );
            onLayoutChange({ ...snapshot.layout, columnOrder: nextOrder });
          }}
          onColumnProposeMove={(from, to) => from !== 0 && to !== 0}
          onKeyDown={handleGridKey}
          onCellContextMenu={([col, row], event) => {
            event.preventDefault();
            if (!itemIsSelected(selection, col, row)) {
              handleSelectionChange({
                columns: CompactSelection.empty(),
                rows: CompactSelection.empty(),
                current: {
                  cell: [col, row],
                  range: { x: col, y: row, width: 1, height: 1 },
                  rangeStack: [],
                },
              });
            }
            setContextMenu({ x: event.localEventX, y: event.localEventY });
          }}
          theme={theme}
        />
        {contextMenu && (
          <div
            className="absolute z-50 min-w-44 rounded-md border border-border bg-bg p-1 text-xs shadow-xl"
            style={{ left: Math.min(contextMenu.x, (wrapperRef.current?.clientWidth ?? 300) - 190), top: Math.min(contextMenu.y, (wrapperRef.current?.clientHeight ?? 300) - 160) }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <ContextButton icon={<Copy className="h-3.5 w-3.5" />} label="Copy" shortcut="Ctrl/Cmd+C" onClick={() => { void gridRef.current?.emit("copy"); setContextMenu(null); }} />
            <ContextButton label="Fill down" shortcut="Ctrl/Cmd+D" disabled={!canEdit} onClick={() => { void gridRef.current?.emit("fill-down"); setContextMenu(null); }} />
            <ContextButton label="Fill right" shortcut="Ctrl/Cmd+R" disabled={!canEdit} onClick={() => { void gridRef.current?.emit("fill-right"); setContextMenu(null); }} />
            <ContextButton label="Set selected to 0" disabled={!canEdit} onClick={() => applyBulk("set", 0)} />
            <ContextButton icon={<Eraser className="h-3.5 w-3.5" />} label="Clear inputs" shortcut="Delete" disabled={!canEdit} onClick={() => applyBulk("clear")} />
          </div>
        )}
      </div>
      <div className="flex items-center justify-between border-x border-b border-border/70 bg-bg px-2 py-1 text-[10px] text-muted">
        <span>Blue cells are inputs · formula cells are locked · right-click for commands</span>
        <span>Drag / Shift-select · Ctrl/Cmd-click for disjoint ranges · paste TSV blocks</span>
      </div>
    </div>
  );
}

function numericValue(
  row: EexQuoteRowComputed | undefined,
  accessor: keyof EexQuoteRowComputed
): number | null {
  const value = row?.[accessor];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function formatMonth(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return MONTH_FORMATTER.format(date).replace(" ", "-");
}

function gridTheme(mode: "light" | "dark"): Partial<Theme> {
  return mode === "dark"
    ? {
        accentColor: "#84b3ef",
        accentFg: "#18202c",
        accentLight: "rgba(132, 179, 239, .19)",
        textDark: "#e9eef6",
        textMedium: "#a1aec2",
        textLight: "#a1aec2",
        textHeader: "#e9eef6",
        textGroupHeader: "#a1aec2",
        bgCell: "#18202c",
        bgCellMedium: "#202b3a",
        bgHeader: "#111720",
        bgHeaderHasFocus: "#22354e",
        bgHeaderHovered: "#22354e",
        borderColor: "#303c4d",
        horizontalBorderColor: "#303c4d",
        headerBottomBorderColor: "#3d4d62",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        baseFontStyle: "12px",
        headerFontStyle: "600 11px",
        markerFontStyle: "11px",
        editorFontSize: "12px",
        cellHorizontalPadding: 9,
      }
    : {
        accentColor: "#285b9c",
        accentFg: "#ffffff",
        accentLight: "rgba(40, 91, 156, .12)",
        textDark: "#1e2b40",
        textMedium: "#637086",
        textLight: "#637086",
        textHeader: "#1e2b40",
        textGroupHeader: "#637086",
        bgCell: "#ffffff",
        bgCellMedium: "#f5f7fa",
        bgHeader: "#f5f7fa",
        bgHeaderHasFocus: "#eaf1fa",
        bgHeaderHovered: "#eaf1fa",
        borderColor: "#dfe5ed",
        horizontalBorderColor: "#dfe5ed",
        headerBottomBorderColor: "#ced7e3",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        baseFontStyle: "12px",
        headerFontStyle: "600 11px",
        markerFontStyle: "11px",
        editorFontSize: "12px",
        cellHorizontalPadding: 9,
      };
}

function inputCellTheme(mode: "light" | "dark"): Partial<Theme> {
  return mode === "dark"
    ? { bgCell: "#11232a", bgCellMedium: "#173039" }
    : { bgCell: "#dbeef3", bgCellMedium: "#cde6ed" };
}

function labelCellTheme(mode: "light" | "dark"): Partial<Theme> {
  return mode === "dark"
    ? { bgCell: "#151925", bgCellMedium: "#1b2130", textDark: "#f0f3f8" }
    : { bgCell: "#f7f7f8", bgCellMedium: "#eceef1", textDark: "#1a1d24" };
}

function physicalCellTheme(mode: "light" | "dark"): Partial<Theme> {
  return mode === "dark"
    ? { bgCell: "#151a25", bgCellMedium: "#202737" }
    : { bgCell: "#fafafa", bgCellMedium: "#f0f1f3" };
}

function analysisCellTheme(mode: "light" | "dark"): Partial<Theme> {
  return mode === "dark"
    ? { bgCell: "#181425", bgCellMedium: "#211a34" }
    : { bgCell: "#faf8ff", bgCellMedium: "#f0ebff" };
}

function totalCellTheme(mode: "light" | "dark"): Partial<Theme> {
  return mode === "dark"
    ? { bgCell: "#33284d", bgCellMedium: "#40325f", textDark: "#ffffff" }
    : { bgCell: "#d0beff", bgCellMedium: "#c4acff", textDark: "#171121" };
}

function cellTheme(column: QuoteColumnSpec, mode: "light" | "dark"): Partial<Theme> | undefined {
  if (column.tone === "input") return inputCellTheme(mode);
  if (column.tone === "physical") return physicalCellTheme(mode);
  if (column.tone === "analysis") return analysisCellTheme(mode);
  return undefined;
}

function ContextButton({
  icon,
  label,
  shortcut,
  disabled,
  onClick,
}: {
  icon?: React.ReactNode;
  label: string;
  shortcut?: string;
  disabled?: boolean;
  onClick(): void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-accent/10 disabled:opacity-40",
        "focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
      )}
    >
      <span className="flex w-4 justify-center">{icon}</span>
      <span>{label}</span>
      {shortcut && <span className="ml-auto text-[10px] text-muted">{shortcut}</span>}
    </button>
  );
}
